import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import HeroSection from '../components/HeroSection'
import FeaturesSection from '../components/FeaturesSection'
import WhyChooseUsSection from '../components/WhyChooseUsSection'

import Navigation from '../components/Navigation'
import SEO from '../components/SEO'
import { 
  Chrome, 
  ArrowRight,
  CheckCircle,
  Star,
  Shield,
  Users,
  ChevronDown,
  BookOpen,
  MessageSquare,
  ArrowUpRight,

} from 'lucide-react'

const HomePage = () => {
  const [activeSection, setActiveSection] = useState('hero')

  const containerRef = useRef<HTMLDivElement>(null)

  // If OAuth tokens end up on home page (shouldn't happen with correct Site URL), just redirect
  useEffect(() => {
    if (window.location.hash && window.location.hash.includes('access_token')) {
      // Don't try to process - just redirect to signin with the hash
      window.location.href = '/signin' + window.location.hash
    }
  }, [])

  // Section tracking with improved scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'features', 'showcase', 'pricing', 'faq', 'cta']
      const scrollPosition = window.scrollY + 150 // Increased offset for better detection

      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])



  // Company logos data
  const companyLogos = [
    {
      name: "Google",
      logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
      width: 100
    },
    {
      name: "Microsoft",
      logo: "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg",
      width: 100
    },
    {
      name: "Netflix",
      logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg",
      width: 100
    },
    {
      name: "Spotify",
      logo: "https://upload.wikimedia.org/wikipedia/commons/1/19/Spotify_logo_without_text.svg",
      width: 100
    },
    {
      name: "Airbnb",
      logo: "https://upload.wikimedia.org/wikipedia/commons/6/69/Airbnb_Logo_Bélo.svg",
      width: 100
    }
  ]





  return (
    <div className="min-h-screen bg-white" ref={containerRef}>
      {/* SEO Component */}
      <SEO 
        title="Chrono Session - AI Powered Browser Session Manager | Organize & Share Tabs"
        description="Organize your browser like never before! Transform tab chaos into smart, shareable sessions with AI-powered organization. Save, restore, and collaborate on browser sessions effortlessly."
        keywords="browser session manager, chrome extension, tab manager, session sharing, AI tab organization, productivity tools, browser productivity, tab management, session saver, chrome tab organizer, browser workflow, smart tabs, session collaboration, browser organization, tab grouping, session analytics, browser efficiency, chrome session manager, tab productivity, browser session sharing, AI browser extension"
      />

      {/* Navigation Component */}
      <Navigation activeSection={activeSection} />

      {/* Hero Section Component */}
      <HeroSection companyLogos={companyLogos} />

      {/* Features Section Component */}
      <FeaturesSection />

      {/* Why Choose Us Section */}
      <WhyChooseUsSection />


      {/* Pricing Section - Google Style */}
      <section id="pricing" className="section-padding bg-white">
        <div className="container-clean">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title text-4xl md:text-5xl mb-6 text-gray-900 font-medium">
              Choose your plan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start for free, upgrade when you need more.
            </p>
          </motion.div>
          
          {/* Pricing Grid - 2 tiers (Google Style) */}
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* Free Plan */}
              <motion.div
                className="relative bg-white rounded-2xl border border-gray-200 p-8 google-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">Free</h3>
                  <div className="mb-4">
                    <span className="text-5xl font-semibold text-gray-900">$0</span>
                    <span className="text-gray-600 ml-2 text-lg">forever</span>
                  </div>
                  <p className="text-gray-600">Perfect for individuals getting started</p>
                </div>

                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-4">Everything you need:</h4>
                  <ul className="space-y-3">
                    {[
                      ["Unlimited", "sessions"],
                      ["Cloud sync", "across devices"], 
                      ["AI thumbnail", "generation (5/day)"],
                      ["Local", "session backup"],
                      ["Smart", "auto-save"]
                    ].map(([bold, text], i) => (
                      <li key={i} className="flex items-center space-x-3">
                        <div className="p-1 rounded-full bg-blue-50">
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        </div>
                        <span className="text-gray-700"><span className="font-semibold text-gray-900">{bold}</span> {text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <a 
                  href="https://chrome.google.com/webstore/detail/mmoeppckalbggegohbocmbkpfmnocpcd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-block text-center py-3 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 google-btn shadow-md hover:shadow-lg"
                >
                  Get Started Free
                </a>
              </motion.div>

              {/* Premium Plan */}
              <motion.div
                className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 p-8 google-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-md">
                    Most Popular
                  </span>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">Premium</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-semibold text-gray-900">Let's talk</span>
                  </div>
                  <p className="text-gray-600">For power users who need more</p>
                </div>

                <div className="mb-8">
                  <ul className="space-y-3 mb-6">
                    {[
                      ["AI generation", "15/day"],
                      ["Advanced", "analytics & insights"],
                      ["Priority", "support"]
                    ].map(([bold, text], i) => (
                      <li key={i} className="flex items-center space-x-3">
                        <div className="p-1 rounded-full bg-blue-50">
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        </div>
                        <span className="text-gray-700"><span className="font-bold text-gray-900">{bold}</span> {text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Expandable Free Features */}
                  <div className="border-t border-blue-100 pt-4">
                    <details className="group">
                      <summary className="flex items-center justify-between cursor-pointer list-none text-sm font-medium text-blue-700 hover:text-blue-800 transition-colors">
                        <span>Everything in Free</span>
                        <ChevronDown className="w-4 h-4 transition-transform duration-300 group-open:rotate-180" />
                      </summary>
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-3 overflow-hidden"
                      >
                        <ul className="space-y-2">
                          {[
                             "Unlimited sessions",
                             "Cloud sync", 
                             "Local backup",
                             "Smart auto-save"
                          ].map((feature, i) => (
                            <li key={i} className="flex items-center space-x-2 text-sm text-gray-600">
                              <CheckCircle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    </details>
                  </div>
                </div>

                <a 
                  href="/contact"
                  className="w-full inline-block text-center py-3 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 google-btn shadow-md hover:shadow-lg"
                >
                  Contact Us
                </a>
              </motion.div>
            </div>

            <p className="text-sm text-gray-500 mt-8 text-center">
              * All plans include unlimited sessions and cloud sync. No hidden fees.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding bg-gray-50">
        <div className="container-clean">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently asked questions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about Chrono Session
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {[
                {
                  question: "Is Chrono Session really free?",
                  answer: "Yes! Chrono Session is completely free to use. You get unlimited sessions, cloud sync, and all core features at no cost. We also offer premium plans for teams with advanced collaboration features."
                },
                {
                  question: "How does cloud sync work?",
                  answer: "Your sessions are automatically saved to the cloud and synced across all your Chrome browsers. Sign in with your Google account on any device and access all your saved sessions instantly."
                },
                {
                  question: "Can I share sessions with my team?",
                  answer: "Absolutely! You can share any session with team members via a simple link. They can view and open the same tabs you saved, making collaboration seamless."
                },
                {
                  question: "What happens to my data if I uninstall?",
                  answer: "Your sessions remain safely stored in the cloud. If you reinstall Chrono Session later, simply sign in and all your data will be restored exactly as you left it."
                },
                {
                  question: "Does it slow down my browser?",
                  answer: "Not at all! Chrono Session is lightweight and optimized for performance. It only activates when you save or restore sessions, so it won't affect your browsing speed."
                },
                {
                  question: "Can I organize my sessions?",
                  answer: "Yes! You can create folders, add tags, search through sessions, and organize them however works best for your workflow. Everything is searchable and easy to find."
                }
              ].map((faq, index) => {
                const [isOpen, setIsOpen] = useState(false)
                
                return (
                  <motion.div
                    key={index}
                    className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <button
                      className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 rounded-2xl transition-colors duration-200"
                      onClick={() => setIsOpen(!isOpen)}
                    >
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex-shrink-0"
                      >
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      </motion.div>
                    </button>
                    
                    <motion.div
                      initial={false}
                      animate={{
                        height: isOpen ? "auto" : 0,
                        opacity: isOpen ? 1 : 0
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut"
                      }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5">
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-gray-600 leading-relaxed mt-3">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )
              })}
            </div>

            <motion.div
              className="text-center mt-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl p-5 sm:p-8 border border-gray-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Still have questions?</h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                  Can't find the answer you're looking for? Our support team is here to help.
                </p>
                <div className="flex flex-col sm:flex-row px-0 sm:px-4 md:px-0 items-stretch sm:items-center justify-center gap-3 sm:gap-4 w-full">
                  <a 
                    href="/contact"
                    className="inline-flex items-center justify-center w-full sm:w-auto px-5 sm:px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors shadow-lgc text-xs md:text-base mb-2 sm:mb-0"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Contact Support
                  </a>
                  <button className="inline-flex items-center justify-center w-full sm:w-auto text-xs md:text-base px-5 sm:px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:border-blue-400 hover:text-blue-600 transition-colors">
                    <BookOpen className="w-5 h-5 mr-2" />
                    View Documentation
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pre-Footer CTA - Redesigned with padding and rounded corners */}
      <section className="py-8 sm:py-10 md:py-12 bg-white">
        <div className="container-clean">
          <motion.div
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl px-4 sm:px-6 md:px-8 py-8 sm:py-10 md:py-16 max-w-full md:max-w-6xl mx-auto google-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center max-w-full md:max-w-4xl mx-auto">
              <h2 className="section-title text-2xl xs:text-3xl sm:text-4xl md:text-6xl mb-4 sm:mb-6 text-gray-900 font-medium leading-tight">
                Ready to organize your browser tabs?
              </h2>
              <p className="text-base xs:text-lg md:text-xl text-gray-600 mb-8 sm:mb-12 leading-relaxed max-w-full sm:max-w-2xl mx-auto">
                Transform your browsing experience today. Install Chrono Session and never lose important tabs again.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-6 sm:mb-8 w-full">
                <motion.a
                  href="https://chrome.google.com/webstore/detail/mmoeppckalbggegohbocmbkpfmnocpcd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full sm:w-auto px-6 xs:px-8 md:px-10 py-3 xs:py-4 bg-gray-900 text-white rounded-full font-semibold text-base xs:text-lg hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Chrome className="w-5 xs:w-6 h-5 xs:h-6 mr-2 xs:mr-3" />
                  <span className="truncate">Add to Chrome - Free</span>
                  <ArrowRight className="w-4 xs:w-5 h-4 xs:h-5 ml-2 xs:ml-3" />
                </motion.a>
                
                <motion.a
                  href="/contact"
                  className="group inline-flex items-center justify-center w-full sm:w-auto px-6 xs:px-8 md:px-8 py-3 xs:py-4 border-2 border-gray-300 text-gray-700 rounded-full font-semibold text-base xs:text-lg hover:border-blue-400 hover:text-blue-600 transition-all duration-200 min-w-[180px] xs:min-w-[200px] md:min-w-[240px]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageSquare className="w-5 xs:w-5 h-5 xs:h-5 mr-2 xs:mr-3" />
                  <span className="truncate">Contact Us</span>
                  <ArrowUpRight className="w-4 xs:w-4 h-4 xs:h-4 ml-1 xs:ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </motion.a>
              </div>
              
              {/* Trust indicators */}
              <div className=" hidden md:flex md:flex-row items-center justify-center gap-3 xs:gap-6 sm:gap-8 text-xs xs:text-sm text-gray-500">
                <div className="flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  <span>Enterprise Security</span>
                </div>
                <div className="flex items-center">
                  <Star className="w-4 h-4 mr-2 text-yellow-400" />
                  <span>4.9/5 Rating</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  <span>100K+ Users</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default HomePage 